import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import {
  emptyAccessRoleSummary,
  summarizeAccessRoles,
  type AccessRoleRow,
} from '@/lib/accessRoles';

export type AppRole =
  | 'admin'
  | 'super_admin'
  | 'campaign_manager'
  | 'bud'
  | 'business_unit_director'
  | 'supervisor'
  | 'finance_viewer'
  | 'line_manager'
  | 'finance'
  | 'brand_ambassador';

export const useUserRole = () => {
  const { user, loading, orgId, orgIdStatus } = useAuth();
  const orgResolutionPending = !!user && (orgIdStatus === 'idle' || orgIdStatus === 'loading');
  const roleQueryEnabled = !!user && !loading && orgIdStatus === 'ready' && !!orgId;
  const { data: fetchedRole, isLoading: roleLoading } = useQuery({
    queryKey: ['user-role', user?.id ?? null, orgId ?? null],
    queryFn: async () => {
      if (!user || !orgId) return null;

      const { data, error } = await supabase
        .from('org_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('org_id', orgId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return data.role as AppRole;
    },
    enabled: roleQueryEnabled,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const role = fetchedRole ?? null;

  const normalizedRole = role;
  const accessLookupEnabled =
    !!user && !loading && !orgResolutionPending && !roleLoading && !!orgId;

  const { data: isPlatformAdmin = false, isLoading: platformAdminLoading } = useQuery({
    queryKey: ['platform-admin-access', user?.id ?? null],
    queryFn: async () => {
      if (!user) return false;

      const { data, error } = await (supabase as any)
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) return false;
      return !!data;
    },
    enabled: accessLookupEnabled,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
    retry: 1,
  });

  const { data: accessRoleSummary = emptyAccessRoleSummary, isLoading: accessRoleLoading } =
    useQuery({
      queryKey: ['user-access-role-summary', user?.id ?? null, orgId ?? null],
      queryFn: async () => {
        if (!user || !orgId) return emptyAccessRoleSummary;

        const { data, error } = await (supabase as any)
          .from('user_roles')
          .select('roles(name, is_super_admin)')
          .eq('user_id', user.id)
          .eq('org_id', orgId)
          .order('role_id', { ascending: true });

        if (error) return emptyAccessRoleSummary;
        return summarizeAccessRoles((data as AccessRoleRow[]) || []);
      },
      enabled: accessLookupEnabled,
      staleTime: 30_000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
      refetchInterval: 30_000,
      retry: 1,
    });

  // This endpoint checks allocated BUD/Admin/Super Admin access on the server.
  // Do not make final-stage approval depend on the role-label metadata query.
  const { data: canApproveBudWages = false, isLoading: budApprovalLoading } = useQuery({
    queryKey: ['bud-approval-access', user?.id ?? null, orgId ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_bud_approval_manager', {
        target_org: orgId!,
      });
      if (error) throw error;
      return data === true;
    },
    enabled: accessLookupEnabled,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
    retry: 1,
  });

  // Allocations are authoritative. Never union in the legacy primary membership role.
  const isSuperAdmin = isPlatformAdmin || accessRoleSummary.isSuperAdmin;
  const isAdmin = accessRoleSummary.isAdmin;
  const isCampaignManager = accessRoleSummary.isCampaignManager;
  const isBud = accessRoleSummary.isBud;
  const isSupervisor = accessRoleSummary.isSupervisor;
  const isFinanceViewer = accessRoleSummary.isFinanceViewer;
  const isLineManager = accessRoleSummary.isLineManager;
  const isFinance = accessRoleSummary.isFinance;
  const isBrandAmbassador =
    role === 'brand_ambassador' && !accessRoleSummary.isManager && !isSuperAdmin;
  const wageApprovalAccess = {
    campaign_manager: isSuperAdmin || isAdmin || isCampaignManager,
    line_manager: canApproveBudWages || isLineManager,
  };
  const isManager =
    isSuperAdmin || isAdmin || isCampaignManager || isBud || isSupervisor || isFinanceViewer;
  const hasRole = useCallback(
    (candidate: AppRole) => {
      switch (candidate) {
        case 'super_admin':
          return isSuperAdmin;
        case 'admin':
          return isAdmin;
        case 'campaign_manager':
          return isCampaignManager;
        case 'bud':
        case 'business_unit_director':
          return isBud;
        case 'supervisor':
          return isSupervisor;
        case 'line_manager':
          return isLineManager;
        case 'finance_viewer':
          return isFinanceViewer;
        case 'finance':
          return isFinance;
        case 'brand_ambassador':
          return isBrandAmbassador;
        default:
          return false;
      }
    },
    [
      isAdmin,
      isBrandAmbassador,
      isBud,
      isCampaignManager,
      isFinanceViewer,
      isFinance,
      isLineManager,
      isSuperAdmin,
      isSupervisor,
    ]
  );

  return {
    role,
    normalizedRole,
    isLoading:
      loading ||
      orgResolutionPending ||
      (roleQueryEnabled && roleLoading) ||
      (accessLookupEnabled && (platformAdminLoading || accessRoleLoading || budApprovalLoading)),
    isSuperAdmin,
    isAdmin,
    isCampaignManager,
    isBud,
    isSupervisor,
    isFinanceViewer,
    isLineManager,
    isFinance,
    wageApprovalAccess,
    isBrandAmbassador,
    isManager,
    hasRole,
  };
};
